import { useParams } from "react-router-dom"
import { useWebPageContent, useUpdateAllWebPageSections, useCreateWebPageVersion } from '@/lib/api/hooks/web-pages';
import Home from "@/components/admin/Content/WebPages/Home/Home";
import { Button, Loader } from "@/components/ui";
import WhoAreWe from "@/components/admin/Content/WebPages/AboutUs/WhoWeAre/WhoWeAre";
import CaretLeftIcon from "@/assets/icons/CaretLeftIcon";
import { ReloadIcon } from "@/assets/icons";
import { Link, useNavigate } from "react-router-dom";
import React, { useState, useEffect, useMemo, useCallback } from "react";
import Breadcrumb from "@/components/ui/Breadcrumb/Breadcrumb";
import CaretRightFilledIcon from "@/assets/icons/CaretRightFilledIcon";
import { Paragraph } from "@/components/ui/Typography";
import OurHistory from "@/components/admin/Content/WebPages/AboutUs/OurHistory/OurHistory";
import { WebPageFormProvider } from "@/contexts/WebPageFormContext";
import Leadership from "@/components/admin/Content/WebPages/AboutUs/Leadership/Leadership";
import LegalFoundation from "@/components/admin/Content/WebPages/AboutUs/LegalFoundation/LegalFoundation";
import MemberCountries from "@/components/admin/Content/WebPages/AboutUs/MemberCountries/MemberCountries";
import StrategicPillars from "@/components/admin/Content/WebPages/ProgrammesAndInitiatives/StrategicPillars/StrategicPillars";
import Programmes from "@/components/admin/Content/WebPages/ProgrammesAndInitiatives/Programmes/Programmes";
import Procurement from "@/components/admin/Content/WebPages/Opportunities/Procurement/Procurement";
import Careers from "@/components/admin/Content/WebPages/Opportunities/Careers/Careers";
import PressRelease from "@/components/admin/Content/WebPages/Media/PressRelease/PressRelease";
import Events from "@/components/admin/Content/WebPages/Events/Events";
import Publications from "@/components/admin/Content/WebPages/Resources/Publications/Publications";
import ContactUs from "@/components/admin/Content/WebPages/ContactUs/ContactUs";
import { showToast } from "@/lib/toast";
import { useForm, useWatch } from "react-hook-form";
import { WebsitePageSection } from "@/types";


const pageConfig: Record<string, { breadcrumb: { label: string, href?: string }[], component: React.ComponentType }> = {
  "who_we_are": {
    breadcrumb: [{ label: 'About Us', href: "#" }, { label: 'Who We Are' }],
    component: WhoAreWe
  },
  "our_history": {
    breadcrumb: [{ label: 'About Us', href: "#" }, { label: 'Our History' }],
    component: OurHistory
  },
  "leadership": {
    breadcrumb: [{ label: 'About Us', href: "#" }, { label: 'Leadership' }],
    component: Leadership
  },
  "legal_foundations": {
    breadcrumb: [{ label: 'About Us', href: "#" }, { label: 'Legal Foundations' }],
    component: LegalFoundation
  },
  "member_countries": {
    breadcrumb: [{ label: 'About Us', href: "#" }, { label: 'Member Countries' }],
    component: MemberCountries
  },
  "strategic_pillars": {
    breadcrumb: [{ label: 'Programmes & Initiatives', href: "#" }, { label: 'Strategic Pillars' }],
    component: StrategicPillars
  },
  "programmes": {
    breadcrumb: [{ label: 'Programmes & Initiatives', href: "#" }, { label: 'Programmes' }],
    component: Programmes
  },
  "procurement": {
    breadcrumb: [{ label: 'Opportunities', href: "#" }, { label: 'Procurement' }],
    component: Procurement
  },
  "careers": {
    breadcrumb: [{ label: 'Opportunities', href: "#" }, { label: 'Careers' }],
    component: Careers
  },
  "press_release": {
    breadcrumb: [{ label: 'Media', href: "#" }, { label: 'Press Release' }],
    component: PressRelease
  },
  "resources": {
    breadcrumb: [{ label: 'Resources', href: "#" }, { label: 'Publication' }],
    component: Publications
  },
  "events": {
    breadcrumb: [{ label: 'Events', href: "#" }],
    component: Events
  },
  "contact_us": {
    breadcrumb: [{ label: 'Contact Us', href: "#" }],
    component: ContactUs
  },
  "home": {
    breadcrumb: [{ label: 'Home' }],
    component: Home
  }
};

const WebPages = () => {
  const navigate = useNavigate();
  const { id, contentId } = useParams<{ id: string, contentId?: string }>();
  const [breadcrumb, setBreadcrumb] = useState<{ label: string, href?: string }[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);

  // Fetch specific version content using contentId
  const { data: pageData, isFetching, isError, refetch: refetchPage } = useWebPageContent(contentId || '');

  const updateAllSectionsMutation = useUpdateAllWebPageSections();
  const createVersionMutation = useCreateWebPageVersion();


  type FormData = {
    sections: Record<string, WebsitePageSection>;
  };

  const {
    setValue,
    reset,
    control,
    getValues
  } = useForm<FormData>({
    defaultValues: {
      sections: {}
    },
    mode: 'onChange'
  });


  const [formSectionsState, setFormSectionsState] = useState<Record<string, WebsitePageSection>>({});


  useWatch({
    control,
    name: 'sections' as const,
    defaultValue: {}
  });

  const pageStatus = pageData?.data?.status || 'DRAFT';

  const activePage = useMemo(() => {
    if (pageData?.data?.websitePageName) {
      return pageData.data.websitePageName.toLowerCase().replace(/ /g, '_');
    }
    return '';
  }, [pageData?.data?.websitePageName]);

  useEffect(() => {
    const pageBreadcrumbs = pageConfig[activePage || '']?.breadcrumb || [];
    setBreadcrumb([
      { label: 'Website Content', href: '/admin/content' },
      { label: 'Versions', href: `/admin/content/web-page/${id}` },
      ...pageBreadcrumbs
    ]);
  }, [activePage, id]);

  useEffect(() => {
    if (pageData?.data?.content) {
      const apiSections = pageData.data.content;
      const apiKeys = Object.keys(apiSections);
      const currentStateKeys = Object.keys(formSectionsState);

      const isEmpty = currentStateKeys.length === 0;

      // A page is "different" if the keys don't match
      const isDifferentContent = apiKeys.length > 0 && (
        currentStateKeys.length !== apiKeys.length ||
        !apiKeys.every(key => currentStateKeys.includes(key))
      );

      // CRITICAL: If we have API data, we should initialize, even if it's empty
      // but only if we haven't initialized yet OR if the content is actually different.
      if (isEmpty || isDifferentContent) {
        // Ensure all sections have their sectionTag properly set
        const sanitizedSections: Record<string, WebsitePageSection> = {};
        Object.entries(apiSections).forEach(([tag, section]) => {
          sanitizedSections[tag] = {
            ...section,
            sectionTag: tag // Force the tag from the key to be sure
          };
        });

        reset({
          sections: sanitizedSections
        }, { keepDefaultValues: false });

        setFormSectionsState(sanitizedSections);
      }
    } else if (pageData?.data && !pageData.data.content && Object.keys(formSectionsState).length === 0) {
      // Handle the case where page exists but has NO content at all (new versions)
      setFormSectionsState({});
      reset({ sections: {} });
    }
  }, [pageData?.data, reset, formSectionsState]);

  const updateLocalSection = useCallback((sectionTag: string, updatedSection: Partial<WebsitePageSection>) => {

    const currentSections = formSectionsState || {};

    const updatedSections = {
      ...currentSections,
      [sectionTag]: {
        ...(currentSections[sectionTag] || {}),
        ...updatedSection,
        sectionTag // Ensure sectionTag is always included for persistence
      }
    };

    setValue('sections', updatedSections, {
      shouldDirty: true,
      shouldValidate: false,
      shouldTouch: true
    });


    setFormSectionsState({ ...updatedSections });
  }, [setValue, formSectionsState]);


  const collectAllSections = () => {
    // Crucial: Use getValues to get the absolute latest state from the form,
    // avoiding the one-render lag of useState updates
    const sectionData = getValues('sections') || {};

    const sections: Array<{
      sectionTitle: string | null;
      title: string | null;
      description: string | null;
      contentType: string | null;
      fileUrl: string | null;
      emailAddress: string | null;
      phoneNumber: string | null;
      phoneCountryCode: string | null;
      address: string | null;
      listOfPurpose: string[] | null;
      imageUrl: string | null;
      sectionTag: string;
    }> = [];

    Object.keys(sectionData).forEach((key) => {
      const section = sectionData[key];
      if (section && typeof section === 'object' && section.sectionTag) {
        sections.push({
          sectionTitle: section.sectionTitle ?? null,
          title: section.title ?? null,
          description: section.description ?? null,
          contentType: section.contentType ?? null,
          fileUrl: section.fileUrl ?? null,
          emailAddress: section.emailAddress ?? null,
          phoneNumber: section.phoneNumber ?? null,
          phoneCountryCode: section.phoneCountryCode ?? null,
          address: section.address ?? null,
          listOfPurpose: section.listOfPurpose ?? null,
          imageUrl: section.imageUrl ?? null,
          sectionTag: section.sectionTag
        });
      }
    });

    return sections;
  };

  const handleSave = async () => {
    if (!id) return;

    const sections = collectAllSections();
    if (sections.length === 0) {
      showToast.error('No sections found to save.');
      return;
    }

    setIsSaving(true);
    try {
      let response;
      if (pageStatus === 'PUBLISH') {
        // Branching: Create a new draft from published content
        response = await createVersionMutation.mutateAsync({
          websitePageId: id,
          data: {
            status: 'DRAFT',
            contents: sections
          }
        });
      } else {
        // Standard Update: Just update the existing draft
        response = await updateAllSectionsMutation.mutateAsync({
          contentId: contentId || '',
          data: {
            status: 'DRAFT',
            contents: sections
          }
        });
      }

      if (response.success) {
        showToast.success(pageStatus === 'PUBLISH' ? 'Original page kept; New draft created' : 'Changes saved to draft');

        // If a new content ID was returned (branching), navigate to it
        if (response.data && typeof response.data === 'object' && 'id' in response.data) {
          const newContentId = response.data.id as string;
          if (newContentId && newContentId !== contentId) {
            navigate(`/admin/content/web-page/${id}/edit/${newContentId}`, { replace: true });
          } else {
            refetchPage();
          }
        } else {
          refetchPage();
        }
      }
    } catch (error) {
      console.error('Save failed:', error);
      showToast.error('Failed to save changes.');
    } finally {
      setIsSaving(false);
    }
  };

  const handlePublish = async () => {
    if (!id) return;

    const sections = collectAllSections();
    if (sections.length === 0) {
      showToast.error('No sections found to publish.');
      return;
    }


    setIsPublishing(true);
    try {
      const response = await updateAllSectionsMutation.mutateAsync({
        contentId: contentId || '',
        data: {
          status: 'PUBLISH',
          contents: sections
        }
      });

      showToast.success('Page published successfully.');

      // Similar to save, navigate to new ID if returned
      if (response.success && response.data && typeof response.data === 'object' && 'id' in response.data) {
        const newContentId = response.data.id as string;
        if (newContentId && newContentId !== contentId) {
          navigate(`/admin/content/web-page/${id}/edit/${newContentId}`, { replace: true });
        } else {
          refetchPage();
        }
      } else {
        refetchPage();
      }
    } catch (error) {
      console.error('Publish failed:', error);
      showToast.error('Failed to publish page.');
    } finally {
      setIsPublishing(false);
    }
  };

  if (id === "undefined" || !id) {
    return <div className="p-4 h-full flex-col gap-4 w-full items-center justify-center flex">
      <Paragraph>Page Not Found</Paragraph>
      <Button onClick={() => navigate(-1)} className="">Go Back</Button>
    </div>;
  }

  const isInitialLoad = !pageData && isFetching;

  if (isInitialLoad) {
    return <Loader size="md" variant="wine" message="Loading page..." />
  }

  if (isError) {
    return <div className="p-4 flex flex-col items-center justify-center h-full w-full gap-4">
      <p>Error fetching data.</p>
      <Button onClick={() => refetchPage()} className="">Retry</Button>
    </div>;
  }

  const renderPage = () => {
    const Component = pageConfig[activePage || '']?.component;
    return Component ? <Component /> : <div className="p-4 h-full w-full items-center justify-center flex">
      <Paragraph>No content available for this page.</Paragraph>
    </div>;
  }

  // Context is loading if we are fetching data AND we haven't initialized the form state yet.
  // Once formSectionsState is initialized (even to empty {}), we stop "loading" the context.
  // We use pageData?.data to check if the main response has arrived.
  const isContextLoading = isFetching || (!pageData?.data && Object.keys(formSectionsState).length === 0);

  return (
    <WebPageFormProvider
      pageData={formSectionsState}
      updateLocalSection={updateLocalSection}
      onSavePage={handleSave}
      isLoading={isContextLoading}
    >
      <div className="grid relative w-full h-full grid-rows-[auto_auto_1fr]">

        {isFetching && !isInitialLoad && (
          <div className="absolute top-0 left-0 right-0 h-1 bg-gray-200 z-20">
            <div className="h-full bg-[#681628] animate-pulse" style={{ width: '30%' }}></div>
          </div>
        )}

        <div className="bg-black p-4 md:px-8 flex items-center justify-between sticky top-0 z-10">
          <div className="flex items-center gap-3">
            <Link to={`/admin/content/web-page/${id}`} className="flex items-center p-2 bg-[#333232] rounded cursor-pointer">
              <CaretLeftIcon fill="white" />
            </Link>
            <Button onClick={() => {
              refetchPage();
            }} variant="ghost" className="text-white hover:text-black">
              <ReloadIcon />
              Reload
            </Button>

            <div className={`px-3 py-1 rounded-md text-xs font-semibold ${pageStatus === 'PUBLISH'
              ? 'bg-green-500 text-white'
              : 'bg-gray-500 text-white'
              }`}>
              {pageStatus === 'PUBLISH' ? 'Published' : 'Draft'}
            </div>
          </div>
          <div className="flex gap-4 items-center">
            <Button
              variant="primary"
              className="disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handlePublish}
              loading={isPublishing}
              disabled={pageStatus !== 'DRAFT' || isSaving || isPublishing}
            >
              Publish
            </Button>
          </div>

        </div>



        <Breadcrumb separator={<CaretRightFilledIcon fill="#00764B" />} items={breadcrumb} containerClassName="p-4 lg:px-12" crumbItemClassName="" linkClassName="!text-[#00764B] hover:underline" />

        <div className="content-scroll-container overflow-y-auto flex-1">
          {renderPage()}
        </div>
      </div>
    </WebPageFormProvider>
  )
}

export default WebPages